import { C } from "../components/reporting/reportingStyles";

/** Mirrors the API's src/const/appBanner.js. */

export const BANNER_TYPES = [
  { value: "image", label: "Image", hint: "One picture, edge to edge. The whole message lives in the artwork." },
  { value: "text", label: "Text", hint: "A title and a paragraph, styled by the app so it always matches the brand." },
  { value: "rich", label: "Rich text", hint: "Formatting, colours and links. Use it when plain text is not enough." },
];

export const BANNER_TYPE_LABELS = Object.fromEntries(BANNER_TYPES.map((t) => [t.value, t.label]));

/**
 * How often ONE person sees a banner. The device keeps this count, so these are promises the
 * app keeps rather than anything the server can verify.
 */
export const FREQUENCIES = [
  { value: "once", label: "Once", hint: "Each person sees it a single time. Editing the message shows it again." },
  { value: "once_per_day", label: "Once a day", hint: "At most once per calendar day, in the customer's own timezone." },
  { value: "every_n_days", label: "Every N days", hint: "At most once in that many days." },
  { value: "every_open", label: "Every time", hint: "On every launch. Use sparingly — it wears out fast." },
  { value: "until_dismissed", label: "Until dismissed", hint: "On every launch until they close it, then never again." },
];

export const FREQUENCY_LABELS = Object.fromEntries(FREQUENCIES.map((f) => [f.value, f.label]));

export const CTA_TYPES = [
  { value: "none", label: "Nothing", hint: "The banner is read and closed." },
  { value: "screen", label: "Open a screen", hint: "Takes the customer somewhere in the app." },
  { value: "url", label: "Open a link", hint: "Opens in the phone's browser." },
];

/**
 * Param-less routes only. The app's root navigate() helper drops params today, so a banner
 * cannot yet open one specific product — which is why "A product" is absent here even though
 * push campaigns offer it.
 */
export const CTA_SCREENS = [
  { value: "home", label: "Home" },
  { value: "loyalty", label: "Loyalty card" },
  { value: "membership", label: "Membership" },
  { value: "orders", label: "Orders" },
  { value: "wallet", label: "Wallet" },
];

export const SCREEN_LABELS = Object.fromEntries(CTA_SCREENS.map((s) => [s.value, s.label]));

export const LIMITS = {
  name: 120,
  title: 160,
  body: 1000,
  html: 8192,
  ctaLabel: 40,
  maxDays: 365,
  imageBytes: 1024 * 1024,
  imageMimeTypes: ["image/jpeg", "image/png", "image/webp"],
};

/** Quill toolbar. Kept in step with the API's allowlist in src/util/sanitizeRichText.js: */
export const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ color: [] }, { background: [] }],
    [{ align: [] }],
    ["link"],
    ["clean"],
  ],
};

/**
 * Anything the admin can produce must be renderable, so this whitelist is the toolbar above
 * and nothing more. A format allowed here but stripped by the server would silently vanish
 * on save; one allowed by the server but missing here simply cannot be typed.
 */
export const QUILL_FORMATS = [
  "header", "bold", "italic", "underline", "strike",
  "list", "bullet", "indent", "color", "background", "align", "link",
];

/**
 * A banner's state is DERIVED, never stored: "off" is the only thing the row knows, and the
 * rest falls out of the window against right now. Storing it would need a cron job to keep
 * a column honest that a comparison answers for free.
 */
export const deriveStatus = (banner, now = new Date()) => {
  if (!banner?.isActive) return "off";
  const from = banner.activeFrom ? new Date(banner.activeFrom) : null;
  const to = banner.activeTo ? new Date(banner.activeTo) : null;
  if (from && from > now) return "scheduled";
  if (to && to < now) return "expired";
  return "live";
};

export const STATUS_META = {
  live: { label: "Live", color: C.green, bg: C.greenLight },
  scheduled: { label: "Scheduled", color: C.blue, bg: C.blueLight },
  expired: { label: "Expired", color: C.textLight, bg: C.bg },
  off: { label: "Off", color: C.textMed, bg: C.borderLight },
};

/** "1 Oct, 14:00 → 8 Oct, 23:59", or "Always on" when both ends are open. */
export const describeWindow = (banner) => {
  const fmt = (v) =>
    new Date(v).toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  if (!banner?.activeFrom && !banner?.activeTo) return "Always on";
  if (!banner.activeFrom) return `Until ${fmt(banner.activeTo)}`;
  if (!banner.activeTo) return `From ${fmt(banner.activeFrom)}`;
  return `${fmt(banner.activeFrom)} → ${fmt(banner.activeTo)}`;
};

export const describeFrequency = (banner) =>
  banner?.frequency === "every_n_days"
    ? `Every ${banner.frequencyDays || "?"} days`
    : FREQUENCY_LABELS[banner?.frequency] || banner?.frequency || "—";

/** Audit labels for the banner history tab (AuditLogPanel, module "marketing"). */
export const BANNER_AUDIT_ACTION_LABELS = {
  create: "Created",
  update: "Edited",
  duplicate: "Duplicated",
  activate: "Switched on",
  deactivate: "Switched off",
};

export const BANNER_AUDIT_FIELD_LABELS = {
  name: "Name",
  bannerType: "Type",
  titleEn: "Title (EN)",
  titleAr: "Title (AR)",
  bodyEn: "Body (EN)",
  bodyAr: "Body (AR)",
  htmlEn: "Formatted text (EN)",
  htmlAr: "Formatted text (AR)",
  imagePath: "Image",
  image: "Image",
  imageWidth: "Image width",
  imageHeight: "Image height",
  cta: "Tap action",
  appId: "App",
  activeFrom: "Starts",
  activeTo: "Ends",
  frequency: "How often",
  frequencyDays: "Days between showings",
  sortOrder: "Display order",
  isActive: "Switched on",
  dismissible: "Can be closed",
  revision: "Revision",
  revisionBumped: "Shown again to everyone",
};

export const bannerAuditResolve = (field, value) => {
  if (field === "bannerType") return BANNER_TYPE_LABELS[value] || value;
  if (field === "frequency") return FREQUENCY_LABELS[value] || value;
  if (field === "cta") {
    if (!value || value.type === "none") return "Nothing";
    if (value.type === "url") return `Link: ${value.url}`;
    return `Screen: ${SCREEN_LABELS[value.screen] || value.screen}`;
  }
  return undefined;
};
